<template>
  <div class="px-3 py-3 pb-10 space-y-4 max-w-screen-2xl mx-auto">
    <!-- ============ Header + refresh ============ -->
    <div class="flex items-center justify-between gap-3">
      <div class="min-w-0">
        <h1 class="text-lg font-bold text-gray-900 dark:text-gray-50 leading-tight">Dashboard</h1>
        <p class="text-xs text-gray-500 dark:text-gray-400 truncate">Live summary across IT support, QA test cases and sprint tickets.</p>
      </div>
      <button
        type="button"
        @click="fetchSummary"
        :disabled="summaryLoading"
        class="shrink-0 inline-flex items-center gap-1.5 rounded-xl bg-amber-400 px-3.5 py-2 text-sm font-semibold text-gray-900 shadow-sm active:scale-95 transition disabled:opacity-60"
      >
        <v-icon size="18" :class="summaryLoading ? 'animate-spin' : ''">mdi-refresh</v-icon>
        Refresh
      </button>
    </div>

    <!-- ============ Overdue monitor ============ -->
    <div
      class="rounded-2xl border p-4"
      :class="overdue.total
        ? 'border-rose-200 bg-rose-50 dark:border-rose-500/30 dark:bg-rose-500/10'
        : 'border-emerald-200 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/10'"
    >
      <div class="flex items-center gap-2.5">
        <v-icon :color="overdue.total ? 'error' : 'success'" size="22">
          {{ overdue.total ? 'mdi-alert-circle-outline' : 'mdi-check-circle-outline' }}
        </v-icon>
        <span class="text-sm font-bold text-gray-900 dark:text-gray-50">
          <template v-if="overdue.total">{{ overdue.total }} overdue {{ overdue.total === 1 ? 'item' : 'items' }} need attention</template>
          <template v-else>Nothing overdue — you're all caught up</template>
        </span>
      </div>
      <div v-if="overdue.total" class="mt-3 flex flex-wrap gap-2">
        <span class="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-700 dark:bg-blue-500/15 dark:text-blue-300">Sprint {{ overdue.sprint }}</span>
        <span class="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-500/15 dark:text-amber-300">IT {{ overdue.it }}</span>
        <span class="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2.5 py-0.5 text-xs font-semibold text-violet-700 dark:bg-violet-500/15 dark:text-violet-300">QA {{ overdue.qa }}</span>
      </div>
    </div>

    <!-- ============ On-time completion monitor ============ -->
    <div class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
      <div class="flex items-start gap-2.5">
        <v-icon :color="onTimeColor(onTime.total.rate)" size="22">mdi-clock-check-outline</v-icon>
        <div class="min-w-0">
          <p class="text-sm font-bold text-gray-900 dark:text-gray-50">
            <template v-if="onTime.total.rate !== null">{{ onTime.total.rate }}% on-time completion</template>
            <template v-else>No completed items with due dates yet</template>
          </p>
          <p v-if="onTime.total.rate !== null" class="text-xs text-gray-500 dark:text-gray-400">
            {{ onTime.total.on_time }} of {{ onTime.total.measured }} met deadline
          </p>
        </div>
      </div>
      <div v-if="onTime.total.measured" class="mt-3 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
        <div
          class="h-full rounded-full transition-all"
          :class="{ success: 'bg-emerald-500', warning: 'bg-amber-400', error: 'bg-rose-500', grey: 'bg-gray-400' }[onTimeColor(onTime.total.rate)]"
          :style="{ width: (onTime.total.rate || 0) + '%' }"
        ></div>
      </div>
      <div v-if="onTime.total.measured" class="mt-3 flex flex-wrap gap-2 text-xs font-semibold">
        <span class="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-gray-700 dark:bg-gray-800 dark:text-gray-300">Sprint {{ moduleOnTimeLabel('sprint') }}</span>
        <span class="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-gray-700 dark:bg-gray-800 dark:text-gray-300">IT {{ moduleOnTimeLabel('it') }}</span>
        <span class="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-gray-700 dark:bg-gray-800 dark:text-gray-300">QA {{ moduleOnTimeLabel('qa') }}</span>
      </div>
    </div>

    <!-- ============ On-time trend (last 6 months) ============ -->
    <div v-if="onTimeTrend.length" class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
      <h2 class="text-sm font-bold text-gray-900 dark:text-gray-50">
        On-time trend <span class="text-xs font-normal text-gray-400">(last 6 months)</span>
      </h2>
      <div class="mt-4 flex gap-2" style="height: 132px">
        <div v-for="p in onTimeTrend" :key="p.period" class="flex flex-1 flex-col items-center gap-1">
          <span class="text-[10px] font-semibold text-gray-500 dark:text-gray-400">{{ p.rate === null ? '—' : p.rate + '%' }}</span>
          <div class="flex w-full flex-1 items-end justify-center">
            <div
              class="w-full max-w-[42px] rounded-t transition-all"
              :class="{ success: 'bg-emerald-500', warning: 'bg-amber-400', error: 'bg-rose-500', grey: 'bg-gray-300 dark:bg-gray-700' }[onTimeColor(p.rate)]"
              :style="{ height: (p.rate === null ? 2 : Math.max(p.rate, 2)) + '%' }"
              :title="p.measured ? (p.on_time + '/' + p.measured + ' met deadline') : 'No completions'"
            ></div>
          </div>
          <span class="text-[10px] text-gray-400">{{ p.label }}</span>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <!-- Sprint tickets summary -->
      <div class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
        <div class="mb-2 flex items-center justify-between">
          <span class="inline-flex items-center gap-1.5 text-sm font-bold text-gray-900 dark:text-gray-50">
            <v-icon size="18" color="info">mdi-clipboard-text-outline</v-icon> Sprint tickets
          </span>
          <span class="text-2xl font-extrabold leading-none text-gray-900 dark:text-gray-50">{{ summary.sprint.total }}</span>
        </div>
        <div class="flex flex-wrap gap-1.5">
          <span class="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600 dark:bg-slate-500/15 dark:text-slate-300">Todo {{ summary.sprint.counts.Todo }}</span>
          <span class="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-700 dark:bg-sky-500/15 dark:text-sky-300">In Progress {{ summary.sprint.counts['In Progress'] }}</span>
          <span class="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:bg-amber-500/15 dark:text-amber-300">On Hold {{ summary.sprint.counts['On Hold'] }}</span>
          <span class="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700 dark:bg-rose-500/15 dark:text-rose-300">Cancelled {{ summary.sprint.counts.Cancelled }}</span>
          <span class="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">Done {{ summary.sprint.counts.Done }}</span>
        </div>
        <p v-if="overdue.sprint" class="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-rose-600 dark:text-rose-400">
          <v-icon size="14" color="error">mdi-alert-circle-outline</v-icon> {{ overdue.sprint }} overdue
        </p>
        <div class="mt-3 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
          <div class="h-full rounded-full bg-emerald-500 transition-all" :style="{ width: pct(summary.sprint.counts.Done, summary.sprint.total) + '%' }"></div>
        </div>
      </div>

      <!-- IT tickets summary -->
      <div class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
        <div class="mb-2 flex items-center justify-between">
          <span class="inline-flex items-center gap-1.5 text-sm font-bold text-gray-900 dark:text-gray-50">
            <v-icon size="18" color="warning">mdi-lifebuoy</v-icon> IT support
          </span>
          <span class="text-2xl font-extrabold leading-none text-gray-900 dark:text-gray-50">{{ summary.it.total }}</span>
        </div>
        <div class="flex flex-wrap gap-1.5">
          <span class="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700 dark:bg-rose-500/15 dark:text-rose-300">Open {{ summary.it.counts.Open }}</span>
          <span class="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-700 dark:bg-sky-500/15 dark:text-sky-300">In Progress {{ summary.it.counts['In Progress'] }}</span>
          <span class="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">Resolved {{ summary.it.counts.Resolved }}</span>
          <span class="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600 dark:bg-slate-500/15 dark:text-slate-300">Closed {{ summary.it.counts.Closed }}</span>
        </div>
        <p v-if="summary.it.overdue" class="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-rose-600 dark:text-rose-400">
          <v-icon size="14" color="error">mdi-alert-circle-outline</v-icon> {{ summary.it.overdue }} overdue
        </p>
        <div class="mt-3 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
          <div class="h-full rounded-full bg-emerald-500 transition-all" :style="{ width: pct(summary.it.counts.Resolved + summary.it.counts.Closed, summary.it.total) + '%' }"></div>
        </div>
      </div>

      <!-- QA cases summary -->
      <div class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
        <div class="mb-2 flex items-center justify-between">
          <span class="inline-flex items-center gap-1.5 text-sm font-bold text-gray-900 dark:text-gray-50">
            <v-icon size="18" color="qa">mdi-test-tube</v-icon> QA test cases
          </span>
          <span class="text-2xl font-extrabold leading-none text-gray-900 dark:text-gray-50">{{ summary.qa.total }}</span>
        </div>
        <div class="flex flex-wrap gap-1.5">
          <span class="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">Pass {{ summary.qa.counts.Pass }}</span>
          <span class="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700 dark:bg-rose-500/15 dark:text-rose-300">Fail {{ summary.qa.counts.Fail }}</span>
          <span class="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:bg-amber-500/15 dark:text-amber-300">Blocked {{ summary.qa.counts.Blocked }}</span>
          <span class="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600 dark:bg-slate-500/15 dark:text-slate-300">Untested {{ summary.qa.counts.Untested }}</span>
        </div>
        <p class="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
          <v-icon size="14" color="success">mdi-check-decagram-outline</v-icon> {{ qaPassRate }}% pass rate
        </p>
        <p v-if="summary.qa.overdue" class="inline-flex items-center gap-1 text-xs font-semibold text-rose-600 dark:text-rose-400">
          <v-icon size="14" color="error">mdi-alert-circle-outline</v-icon> {{ summary.qa.overdue }} overdue
        </p>
        <div class="mt-3 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
          <div class="h-full rounded-full bg-emerald-500 transition-all" :style="{ width: qaPassRate + '%' }"></div>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 gap-3 lg:grid-cols-12">
      <!-- ============ Today's tasks ============ -->
      <section class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900 lg:col-span-7">
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <h2 class="text-sm font-bold text-gray-900 dark:text-gray-50">Today's tasks</h2>
            <p class="text-xs text-gray-500 dark:text-gray-400">due/overdue, active IT, and QA needing a run</p>
          </div>
          <div class="flex shrink-0 items-center gap-2">
            <!-- Segmented control drives the same `mineOnly` state as before -->
            <div class="inline-flex rounded-lg bg-gray-100 p-0.5 text-xs font-semibold dark:bg-gray-800">
              <button type="button" @click="mineOnly = false"
                class="rounded-md px-2.5 py-1 transition"
                :class="!mineOnly ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-gray-50' : 'text-gray-500 dark:text-gray-400'">All</button>
              <button type="button" @click="mineOnly = true"
                class="rounded-md px-2.5 py-1 transition"
                :class="mineOnly ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-gray-50' : 'text-gray-500 dark:text-gray-400'">Mine</button>
            </div>
            <span class="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-amber-400 px-1.5 text-xs font-bold text-gray-900">{{ filteredTodayTasks.length }}</span>
          </div>
        </div>

        <!-- Loading skeletons -->
        <div v-if="summaryLoading" class="mt-3 space-y-2">
          <div v-for="n in 4" :key="'ts-' + n" class="h-20 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800"></div>
        </div>

        <!-- Empty -->
        <EmptyState v-else-if="!filteredTodayTasks.length" dense icon="mdi-check-circle-outline"
          :title="mineOnly ? 'Nothing assigned to you today' : 'Nothing needs attention today'" />

        <!-- Issue cards -->
        <div v-else class="mt-3 space-y-2">
          <div
            v-for="(item, index) in filteredTodayTasks"
            :key="'task-' + item.type + '-' + item.ref + '-' + index"
            class="rounded-xl border border-gray-200 bg-white p-3 transition active:bg-gray-50 dark:border-gray-800 dark:bg-gray-900 dark:active:bg-gray-800"
          >
            <!-- Header: ref + type/overdue badges -->
            <div class="flex items-center justify-between gap-2">
              <span class="font-mono text-xs font-bold text-gray-500 dark:text-gray-400">{{ item.ref }}</span>
              <div class="flex items-center gap-1.5">
                <span
                  class="rounded-full px-2 py-0.5 text-[11px] font-semibold"
                  :class="{
                    Sprint: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300',
                    IT: 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300',
                    QA: 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300',
                  }[item.type]"
                >{{ item.type }}</span>
                <span v-if="item.overdue" class="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-rose-700 dark:bg-rose-500/15 dark:text-rose-300">Overdue</span>
              </div>
            </div>

            <!-- Body: bold task title as focal point -->
            <p class="mt-1.5 text-sm font-semibold leading-snug text-gray-900 dark:text-gray-50">{{ item.title }}</p>

            <!-- Footer: assignee + status + due -->
            <div class="mt-2.5 flex items-center justify-between gap-2">
              <div class="flex min-w-0 items-center gap-2">
                <span class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-100 text-[10px] font-bold text-amber-800 dark:bg-amber-500/20 dark:text-amber-300">{{ initials(item.assignee) }}</span>
                <span class="truncate text-xs text-gray-600 dark:text-gray-300">{{ item.assignee || 'Unassigned' }}</span>
              </div>
              <div class="flex shrink-0 items-center gap-2">
                <span
                  class="rounded-full px-2 py-0.5 text-[11px] font-semibold"
                  :class="{
                    success: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
                    error: 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300',
                    warning: 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300',
                    info: 'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300',
                    neutral: 'bg-slate-100 text-slate-600 dark:bg-slate-500/15 dark:text-slate-300',
                  }[statusColor(item.status)]"
                >{{ item.status }}</span>
                <span v-if="item.due_date" class="text-xs" :class="item.overdue ? 'font-semibold text-rose-600 dark:text-rose-400' : 'text-gray-500 dark:text-gray-400'">{{ formattedDate(item.due_date) }}</span>
                <span v-else class="text-xs text-gray-400">—</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ============ Member KPIs ============ -->
      <section class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900 lg:col-span-5">
        <div class="flex items-start justify-between gap-2">
          <div class="min-w-0">
            <h2 class="text-sm font-bold text-gray-900 dark:text-gray-50">Member KPIs</h2>
            <p class="text-xs text-gray-500 dark:text-gray-400">open load, completed, overdue, QA pass rate, avg resolution (IT / Sprint / Test cases)</p>
          </div>
          <button v-if="members.length" type="button" @click="exportKpiCsv" title="Export to CSV"
            class="inline-flex shrink-0 items-center gap-1 rounded-lg bg-gray-100 px-2.5 py-1.5 text-xs font-semibold text-gray-700 transition active:scale-95 dark:bg-gray-800 dark:text-gray-200">
            <v-icon size="15">mdi-download</v-icon>Export
          </button>
        </div>

        <!-- Loading skeletons -->
        <div v-if="summaryLoading" class="mt-3 space-y-2">
          <div v-for="n in 3" :key="'ms-' + n" class="h-28 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800"></div>
        </div>

        <!-- Empty -->
        <EmptyState v-else-if="!members.length" dense icon="mdi-account-group-outline" title="No member activity yet" />

        <!-- Member cards -->
        <div v-else class="mt-3 space-y-2">
          <div
            v-for="(item, index) in members"
            :key="'member-' + index"
            class="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-gray-900"
          >
            <div class="flex items-center gap-2.5">
              <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-xs font-bold text-amber-800 dark:bg-amber-500/20 dark:text-amber-300">{{ initials(item.name) }}</span>
              <div class="min-w-0 flex-1">
                <p class="truncate text-sm font-bold text-gray-900 dark:text-gray-50">{{ item.name }}</p>
                <p v-if="item.team" class="truncate text-xs text-gray-500 dark:text-gray-400">{{ item.team }}</p>
              </div>
              <span v-if="item.overdue" class="shrink-0 rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-bold text-rose-700 dark:bg-rose-500/15 dark:text-rose-300">{{ item.overdue }} overdue</span>
            </div>

            <div class="mt-3 grid grid-cols-3 gap-2 text-center">
              <div class="rounded-lg bg-gray-100 px-2 py-1.5 dark:bg-gray-800">
                <p class="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Open</p>
                <p class="text-sm font-bold text-gray-900 dark:text-gray-50">{{ item.open_load }}</p>
              </div>
              <div class="rounded-lg bg-gray-100 px-2 py-1.5 dark:bg-gray-800">
                <p class="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Done</p>
                <p class="text-sm font-bold text-gray-900 dark:text-gray-50">{{ item.completed }}</p>
              </div>
              <div class="rounded-lg bg-gray-100 px-2 py-1.5 dark:bg-gray-800">
                <p class="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">QA %</p>
                <p class="text-sm font-bold text-gray-900 dark:text-gray-50">{{ item.qa_pass_rate !== null ? item.qa_pass_rate + '%' : '—' }}</p>
              </div>
              <div class="rounded-lg bg-gray-100 px-2 py-1.5 dark:bg-gray-800">
                <p class="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">IT res</p>
                <p class="text-sm font-bold text-gray-900 dark:text-gray-50">{{ item.avg_it_hours !== null && item.avg_it_hours !== undefined ? formatResolution(item.avg_it_hours) : '—' }}</p>
              </div>
              <div class="rounded-lg bg-gray-100 px-2 py-1.5 dark:bg-gray-800">
                <p class="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Sprint res</p>
                <p class="text-sm font-bold text-gray-900 dark:text-gray-50">{{ item.avg_sprint_hours !== null && item.avg_sprint_hours !== undefined ? formatResolution(item.avg_sprint_hours) : '—' }}</p>
              </div>
              <div class="rounded-lg bg-gray-100 px-2 py-1.5 dark:bg-gray-800">
                <p class="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">TC res</p>
                <p class="text-sm font-bold text-gray-900 dark:text-gray-50">{{ item.avg_qa_hours !== null && item.avg_qa_hours !== undefined ? formatResolution(item.avg_qa_hours) : '—' }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- ============ Sprint focus (burndown + workload) ============ -->
    <div class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
      <div class="mb-3 flex items-center justify-between gap-2">
        <h2 class="text-sm font-bold text-gray-900 dark:text-gray-50">Sprint focus</h2>
        <span v-if="progress"
          class="rounded-full px-2.5 py-0.5 text-xs font-bold"
          :class="progress.percent_complete === 100
            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300'
            : 'bg-amber-400 text-gray-900'">
          {{ progress.percent_complete }}% complete
        </span>
      </div>
      <div class="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <v-autocomplete
          density="compact" variant="solo-filled" flat hide-details label="Project"
          :items="projects" item-title="title" item-value="id"
          v-model="selectedProject" @update:modelValue="onProjectChange"
        ></v-autocomplete>
        <v-autocomplete
          density="compact" variant="solo-filled" flat hide-details label="Sprint"
          :items="sprints" item-title="name" item-value="id"
          v-model="selectedSprint" :disabled="!selectedProject"
          @update:modelValue="getProgress"
        ></v-autocomplete>
        <v-autocomplete
          density="compact" variant="solo-filled" flat hide-details label="Compare with"
          clearable
          :items="comparableSprints" item-title="name" item-value="id"
          v-model="compareSprint" :disabled="!selectedSprint"
          @update:modelValue="getCompareProgress"
        ></v-autocomplete>
      </div>
    </div>

    <div class="grid grid-cols-1 gap-3 lg:grid-cols-12">
      <!-- Burndown chart -->
      <div class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900 lg:col-span-7">
        <h2 class="text-sm font-bold text-gray-900 dark:text-gray-50">
          Sprint Burndown <span class="text-xs font-normal text-gray-400">(ideal vs actual remaining tickets)</span>
        </h2>
        <div v-if="progress && progress.burndown.length" class="mt-2">
          <svg :viewBox="`0 0 ${chart.w} ${chart.h}`" width="100%" style="max-height: 240px">
            <line :x1="chart.padL" :y1="chart.padT" :x2="chart.padL" :y2="chart.h - chart.padB" stroke="#ccc" />
            <line :x1="chart.padL" :y1="chart.h - chart.padB" :x2="chart.w - chart.padR" :y2="chart.h - chart.padB" stroke="#ccc" />
            <text :x="chart.padL - 6" :y="chart.padT + 4" font-size="10" text-anchor="end" fill="#888">{{ progress.total }}</text>
            <text :x="chart.padL - 6" :y="chart.h - chart.padB + 4" font-size="10" text-anchor="end" fill="#888">0</text>
            <polyline :points="idealPoints" fill="none" stroke="#9e9e9e" stroke-width="1.5" stroke-dasharray="5,4" />
            <polyline :points="actualPoints" fill="none" stroke="#fb8c00" stroke-width="2" />
            <polyline v-if="compareProgress" :points="compareActualPoints" fill="none" stroke="#00897b" stroke-width="2" stroke-dasharray="2,2" />
          </svg>
          <div class="flex flex-wrap justify-center gap-3 text-xs text-gray-500 dark:text-gray-400">
            <span class="inline-flex items-center gap-1"><v-icon size="x-small" color="#9e9e9e">mdi-minus</v-icon> Ideal</span>
            <span class="inline-flex items-center gap-1"><v-icon size="x-small" color="#fb8c00">mdi-minus</v-icon> {{ currentSprintName }}</span>
            <span v-if="compareProgress" class="inline-flex items-center gap-1"><v-icon size="x-small" color="#00897b">mdi-minus</v-icon> {{ compareSprintName }}</span>
          </div>
        </div>
        <p v-else class="mt-2 text-sm text-gray-500 dark:text-gray-400">No active sprint to chart.</p>
      </div>

      <!-- Status breakdown -->
      <div class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900 lg:col-span-5">
        <h2 class="text-sm font-bold text-gray-900 dark:text-gray-50">Status breakdown</h2>
        <div v-if="progress" class="mt-3 space-y-2.5">
          <div v-for="(count, status) in progress.status_counts" :key="status">
            <div class="mb-1 flex items-center justify-between text-xs">
              <span class="font-medium text-gray-700 dark:text-gray-300">{{ status }}</span>
              <span class="text-gray-400">{{ count }}</span>
            </div>
            <div class="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div
                class="h-full rounded-full transition-all"
                :class="{ success: 'bg-emerald-500', error: 'bg-rose-500', warning: 'bg-amber-400', info: 'bg-sky-500', neutral: 'bg-slate-400' }[statusColor(status)]"
                :style="{ width: (progress.total ? (count / progress.total) * 100 : 0) + '%' }"
              ></div>
            </div>
          </div>
        </div>
        <p v-else class="mt-2 text-sm text-gray-500 dark:text-gray-400">Select a sprint to see the breakdown.</p>
      </div>

      <!-- Per-assignee workload -->
      <div class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900 lg:col-span-12">
        <h2 class="text-sm font-bold text-gray-900 dark:text-gray-50">
          Team workload <span class="text-xs font-normal text-gray-400">(this sprint)</span>
        </h2>
        <div v-if="workload.length" class="mt-3 space-y-3">
          <div v-for="row in workload" :key="row.id === null ? 'unassigned' : row.id">
            <div class="mb-1 flex items-center justify-between gap-2 text-xs">
              <span class="inline-flex items-center gap-1 font-medium text-gray-700 dark:text-gray-300">
                <v-icon size="14">mdi-account</v-icon> {{ row.name }}
              </span>
              <span class="text-gray-400 dark:text-gray-500">
                {{ row.done }} done · {{ row.in_progress }} in progress · {{ row.remaining }} left
                <span class="ml-1 font-bold text-gray-700 dark:text-gray-300">({{ row.total }})</span>
              </span>
            </div>
            <div class="relative h-3 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div class="absolute inset-y-0 left-0 rounded-full bg-sky-400" :style="{ width: (row.total ? ((row.done + row.in_progress) / row.total) * 100 : 0) + '%' }"></div>
              <div class="absolute inset-y-0 left-0 rounded-full bg-emerald-500" :style="{ width: (row.total ? (row.done / row.total) * 100 : 0) + '%' }"></div>
            </div>
          </div>
        </div>
        <p v-else class="mt-2 text-sm text-gray-500 dark:text-gray-400">No tickets assigned in this sprint.</p>
      </div>

      <!-- Sprint velocity report -->
      <div class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900 lg:col-span-12">
        <div class="flex items-start justify-between gap-2">
          <h2 class="text-sm font-bold text-gray-900 dark:text-gray-50">
            Sprint velocity <span class="text-xs font-normal text-gray-400">(committed vs completed)</span>
          </h2>
          <button v-if="velocity.length" type="button" @click="exportVelocityCsv" title="Export to CSV"
            class="inline-flex shrink-0 items-center gap-1 rounded-lg bg-gray-100 px-2.5 py-1.5 text-xs font-semibold text-gray-700 transition active:scale-95 dark:bg-gray-800 dark:text-gray-200">
            <v-icon size="15">mdi-download</v-icon>Export
          </button>
        </div>
        <div v-if="velocity.length" class="mt-3 space-y-3">
          <div v-for="s in velocity" :key="s.id">
            <div class="mb-1 flex items-center justify-between gap-2 text-xs">
              <span class="inline-flex min-w-0 items-center gap-2 font-medium text-gray-700 dark:text-gray-300">
                <span class="truncate">{{ s.name }}</span>
                <span class="shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold capitalize"
                  :class="{ active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300', completed: 'bg-slate-100 text-slate-600 dark:bg-slate-500/15 dark:text-slate-300', planned: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300' }[s.status] || 'bg-slate-100 text-slate-600 dark:bg-slate-500/15 dark:text-slate-300'">{{ s.status }}</span>
              </span>
              <span class="shrink-0 text-gray-400 dark:text-gray-500">
                {{ s.completed }}/{{ s.committed }} done
                <span class="ml-1 font-bold text-gray-700 dark:text-gray-300">{{ s.completion_rate === null ? '—' : s.completion_rate + '%' }}</span>
              </span>
            </div>
            <div class="h-3 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div class="h-full rounded-full bg-emerald-500 transition-all" :style="{ width: (s.committed ? (s.completed / s.committed) * 100 : 0) + '%' }"></div>
            </div>
          </div>
        </div>
        <p v-else class="mt-2 text-sm text-gray-500 dark:text-gray-400">No sprints to report for this project.</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import utils from '@/utils';
import constant from '@/constant';
import apiMixin from '@/mixins/apiMixin';
import formatMixin from '@/mixins/formatMixin';
import EmptyState from '@/components/ui/EmptyState.vue';
import exportCsv from '@/utils/exportCsv';

const emptySummary = () => ({
  sprint: { counts: { Todo: 0, 'In Progress': 0, 'On Hold': 0, Cancelled: 0, Done: 0 }, total: 0, overdue: 0 },
  it: { counts: { Open: 0, 'In Progress': 0, Resolved: 0, Closed: 0 }, total: 0, overdue: 0 },
  qa: { counts: { Pass: 0, Fail: 0, Blocked: 0, Skipped: 0, Untested: 0 }, total: 0, overdue: 0 },
  overdue: { sprint: 0, it: 0, qa: 0, total: 0 },
  on_time: {
    sprint: { on_time: 0, measured: 0, rate: null },
    it: { on_time: 0, measured: 0, rate: null },
    qa: { on_time: 0, measured: 0, rate: null },
    total: { on_time: 0, measured: 0, rate: null },
  },
});

export default {
  name: 'OverviewPage',
  mixins: [apiMixin, formatMixin],
  components: { EmptyState },
  data: () => ({
    constant: constant,
    user_data: null,
    // Cross-module dashboard payload
    summary: emptySummary(),
    todayTasks: [],
    mineOnly: false,
    members: [],
    onTimeTrend: [],
    summaryLoading: false,
    taskHeaders: [
      { title: 'Type', key: 'type', sortable: true },
      { title: 'Ref', key: 'ref', sortable: true },
      { title: 'Title', key: 'title', sortable: false },
      { title: 'Assignee', key: 'assignee', sortable: true },
      { title: 'Status', key: 'status', sortable: true },
      { title: 'Due', key: 'due_date', sortable: true },
    ],
    memberHeaders: [
      { title: 'Member', key: 'name', sortable: true },
      { title: 'Open', key: 'open_load', sortable: true, align: 'center' },
      { title: 'Done', key: 'completed', sortable: true, align: 'center' },
      { title: 'Overdue', key: 'overdue', sortable: true, align: 'center' },
      { title: 'QA %', key: 'qa_pass_rate', sortable: true, align: 'center' },
      { title: 'IT res', key: 'avg_it_hours', sortable: true, align: 'center' },
      { title: 'Sprint res', key: 'avg_sprint_hours', sortable: true, align: 'center' },
      { title: 'TC res', key: 'avg_qa_hours', sortable: true, align: 'center' },
    ],
    // Sprint focus
    projects: [],
    sprints: [],
    selectedProject: null,
    selectedSprint: null,
    compareSprint: null,
    progress: null,
    compareProgress: null,
    workload: [],
    velocity: [],
    chart: { w: 520, h: 220, padL: 30, padR: 12, padT: 12, padB: 24 },
  }),
  computed: {
    // Logged-in user's display name, used to filter "Today's tasks" to mine.
    myName() {
      const u = this.user_data || {};
      if (u.employee_information && u.employee_information.fullname) {
        return u.employee_information.fullname;
      }
      return u.email ? u.email.split('@')[0] : '';
    },
    // Today's tasks, optionally narrowed to items assigned to me.
    filteredTodayTasks() {
      if (!this.mineOnly) return this.todayTasks;
      const name = (this.myName || '').toLowerCase();
      if (!name) return this.todayTasks;
      return this.todayTasks.filter(
        (t) => (t.assignee || '').toLowerCase().includes(name)
      );
    },
    // Cross-module overdue breakdown (strict: due before today, not finished).
    overdue() {
      return this.summary.overdue || { sprint: 0, it: 0, qa: 0, total: 0 };
    },
    // Cross-module on-time completion (% of finished items that met their due date).
    onTime() {
      const empty = { on_time: 0, measured: 0, rate: null };
      const ot = this.summary.on_time || {};
      return {
        sprint: ot.sprint || { ...empty },
        it: ot.it || { ...empty },
        qa: ot.qa || { ...empty },
        total: ot.total || { ...empty },
      };
    },
    qaPassRate() {
      const c = this.summary.qa.counts;
      const run = c.Pass + c.Fail + c.Blocked + c.Skipped;
      return run ? Math.round((c.Pass / run) * 100) : 0;
    },
    plotW() { return this.chart.w - this.chart.padL - this.chart.padR; },
    plotH() { return this.chart.h - this.chart.padT - this.chart.padB; },
    yMax() {
      const a = this.progress ? this.progress.total : 0;
      const b = this.compareProgress ? this.compareProgress.total : 0;
      return Math.max(a, b, 1);
    },
    comparableSprints() {
      return this.sprints.filter(s => s.id !== this.selectedSprint);
    },
    currentSprintName() {
      const s = this.sprints.find(x => x.id === this.selectedSprint);
      return s ? s.name : 'Actual';
    },
    compareSprintName() {
      const s = this.sprints.find(x => x.id === this.compareSprint);
      return s ? s.name : 'Comparison';
    },
    compareActualPoints() {
      if (!this.compareProgress) return '';
      const n = this.compareProgress.burndown.length;
      return this.compareProgress.burndown
        .map((p, i) => ({ p, i }))
        .filter(({ p }) => p.remaining !== null && p.remaining !== undefined)
        .map(({ p, i }) => {
          const x = this.chart.padL + (n > 1 ? (i / (n - 1)) * this.plotW : 0);
          const y = this.chart.padT + (1 - p.remaining / this.yMax) * this.plotH;
          return `${x.toFixed(1)},${y.toFixed(1)}`;
        }).join(' ');
    },
    idealPoints() {
      if (!this.progress) return '';
      const n = this.progress.burndown.length;
      return this.progress.burndown.map((p, i) => {
        const x = this.chart.padL + (n > 1 ? (i / (n - 1)) * this.plotW : 0);
        const y = this.chart.padT + (1 - p.ideal / this.yMax) * this.plotH;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      }).join(' ');
    },
    actualPoints() {
      if (!this.progress) return '';
      const n = this.progress.burndown.length;
      return this.progress.burndown
        .map((p, i) => ({ p, i }))
        .filter(({ p }) => p.remaining !== null && p.remaining !== undefined)
        .map(({ p, i }) => {
          const x = this.chart.padL + (n > 1 ? (i / (n - 1)) * this.plotW : 0);
          const y = this.chart.padT + (1 - p.remaining / this.yMax) * this.plotH;
          return `${x.toFixed(1)},${y.toFixed(1)}`;
        }).join(' ');
    },
  },
  methods: {
    // Download the current Member KPI table as a CSV report.
    exportKpiCsv() {
      const cols = [
        ['name', 'Member'],
        ['team', 'Team'],
        ['open_load', 'Open load'],
        ['completed', 'Completed'],
        ['overdue', 'Overdue'],
        ['qa_pass_rate', 'QA pass rate %'],
        ['avg_it_hours', 'IT resolution'],
        ['avg_sprint_hours', 'Sprint resolution'],
        ['avg_qa_hours', 'Test case resolution'],
      ];
      const esc = (v) => '"' + String(v === null || v === undefined ? '' : v).replace(/"/g, '""') + '"';
      const cell = (row, key) => {
        if (key === 'qa_pass_rate') return row.qa_pass_rate === null ? '' : row.qa_pass_rate;
        if (key === 'avg_it_hours' || key === 'avg_sprint_hours' || key === 'avg_qa_hours') {
          return (row[key] === null || row[key] === undefined) ? '' : this.formatResolution(row[key]);
        }
        return row[key];
      };
      const lines = [cols.map((c) => esc(c[1])).join(',')];
      this.members.forEach((row) => lines.push(cols.map((c) => esc(cell(row, c[0]))).join(',')));
      exportCsv(`member_kpis_${new Date().toISOString().slice(0, 10)}.csv`, lines.join('\n'));
    },
    // authHeaders, formattedDate, pct, initials, typeColor and statusColor now
    // come from the shared apiMixin / formatMixin (single source of truth).
    // Color a completion rate: green when healthy, amber/red as it slips.
    onTimeColor(rate) {
      if (rate === null || rate === undefined) return 'grey';
      if (rate >= 85) return 'success';
      if (rate >= 60) return 'warning';
      return 'error';
    },
    // Per-module chip label: show rate when measured, otherwise a dash.
    moduleOnTimeLabel(key) {
      const m = this.onTime[key];
      if (!m || !m.measured) return '—';
      return `${m.rate}%`;
    },
    // Humanize an average resolution time (in hours) into m / h / d h.
    formatResolution(hours) {
      if (hours === null || hours === undefined) return '—';
      if (hours < 1) return `${Math.round(hours * 60)}m`;
      if (hours < 24) return `${Math.round(hours * 10) / 10}h`;
      const days = Math.floor(hours / 24);
      const rem = Math.round(hours - days * 24);
      return rem ? `${days}d ${rem}h` : `${days}d`;
    },
    fetchSummary() {
      this.summaryLoading = true;
      axios.get(utils._api(this.constant.dashboard_summary), this.authHeaders())
        .then((response) => {
          const data = response.data.data || {};
          this.summary = data.summaries || emptySummary();
          this.todayTasks = data.today_tasks || [];
          this.members = data.members || [];
        })
        .catch(error => console.error('Error fetching dashboard summary:', error))
        .finally(() => { this.summaryLoading = false; });
    },
    fetchOnTimeTrend() {
      axios.get(utils._api(this.constant.dashboard_on_time_trend), this.authHeaders())
        .then((response) => { this.onTimeTrend = response.data.data || []; })
        .catch(error => console.error('Error fetching on-time trend:', error));
    },
    getProjects() {
      axios.get(utils._api(this.constant.list_projects), this.authHeaders())
        .then((response) => {
          this.projects = response.data.data || [];
          if (this.projects.length && !this.selectedProject) {
            this.selectedProject = this.projects[0].id;
            this.onProjectChange();
          }
        })
        .catch(error => console.error('Error fetching projects:', error));
    },
    onProjectChange() {
      this.selectedSprint = null;
      this.compareSprint = null;
      this.sprints = [];
      this.progress = null;
      this.compareProgress = null;
      this.workload = [];
      this.velocity = [];
      if (!this.selectedProject) return;
      this.getVelocity();
      axios.post(utils._api(this.constant.list_sprints),
        { project_id: this.selectedProject }, this.authHeaders())
        .then((response) => {
          this.sprints = response.data.data || [];
          const active = this.sprints.find(s => s.status === 'active');
          this.selectedSprint = active ? active.id : (this.sprints[0]?.id ?? null);
          if (this.selectedSprint) this.getProgress();
        })
        .catch(error => console.error('Error fetching sprints:', error));
    },
    getProgress() {
      this.compareSprint = null;
      this.compareProgress = null;
      if (!this.selectedSprint) { this.progress = null; this.workload = []; return; }
      axios.post(utils._api(this.constant.sprint_progress),
        { sprint_id: this.selectedSprint }, this.authHeaders())
        .then((response) => { this.progress = response.data.data; })
        .catch(error => console.error('Error fetching progress:', error));
      this.getWorkload();
    },
    getWorkload() {
      if (!this.selectedSprint) { this.workload = []; return; }
      axios.post(utils._api(this.constant.sprint_workload),
        { sprint_id: this.selectedSprint }, this.authHeaders())
        .then((response) => { this.workload = response.data.data || []; })
        .catch(error => console.error('Error fetching workload:', error));
    },
    // Per-sprint committed vs completed for the selected project.
    getVelocity() {
      if (!this.selectedProject) { this.velocity = []; return; }
      axios.post(utils._api(this.constant.sprint_velocity),
        { project_id: this.selectedProject }, this.authHeaders())
        .then((response) => { this.velocity = response.data.data || []; })
        .catch(error => console.error('Error fetching velocity:', error));
    },
    // Download the sprint velocity report as CSV.
    exportVelocityCsv() {
      const cols = [
        ['name', 'Sprint'],
        ['status', 'Status'],
        ['project', 'Project'],
        ['start_date', 'Start'],
        ['end_date', 'End'],
        ['committed', 'Committed'],
        ['completed', 'Completed'],
        ['completion_rate', 'Completion %'],
      ];
      const esc = (v) => '"' + String(v === null || v === undefined ? '' : v).replace(/"/g, '""') + '"';
      const lines = [cols.map((c) => esc(c[1])).join(',')];
      this.velocity.forEach((row) => lines.push(cols.map((c) => esc(row[c[0]])).join(',')));
      exportCsv(`sprint_velocity_${new Date().toISOString().slice(0, 10)}.csv`, lines.join('\n'));
    },
    getCompareProgress() {
      if (!this.compareSprint) { this.compareProgress = null; return; }
      axios.post(utils._api(this.constant.sprint_progress),
        { sprint_id: this.compareSprint }, this.authHeaders())
        .then((response) => { this.compareProgress = response.data.data; })
        .catch(error => console.error('Error fetching comparison:', error));
    },
  },
  created() {
    const userData = localStorage.getItem('user_data');
    if (userData) {
      try { this.user_data = JSON.parse(userData); } catch (e) { this.user_data = null; }
    }
  },
  mounted() {
    this.fetchSummary();
    this.fetchOnTimeTrend();
    this.getProjects();
  }
}
</script>

<style scoped>
.ov-card {
  border-radius: 14px;
}
.ov-overdue-card {
  border: 1px solid rgba(211, 47, 47, 0.4);
  background: rgba(211, 47, 47, 0.04);
}
.ov-card-title {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--int-text-strong);
}
.ov-card-total {
  font-size: 1.6rem;
  font-weight: 800;
  color: var(--int-text-strong);
  line-height: 1;
}
.ov-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.ov-flag {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--int-text-muted);
}
.ov-section-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--int-text-strong);
}
.ov-section-sub {
  display: block;
  font-size: 0.72rem;
  font-weight: 400;
  color: var(--int-text-faint);
}
.ov-table :deep(th) {
  font-weight: 700 !important;
  color: var(--int-text-strong) !important;
}
.ov-table :deep(td) {
  font-size: 0.82rem;
}
.ov-clip {
  display: inline-block;
  max-width: 260px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}
.ov-member-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--int-text-strong);
}
.ov-overdue {
  font-size: 0.68rem;
  font-weight: 700;
  color: #d32f2f;
  text-transform: uppercase;
}
</style>
